import { trackCustomEvent } from 'utilities/app-insights';

import {
  getAddEditCardRequest,
  getEditCardDetails,
  getFleetLimiterCodesObj,
  isReissueForm,
  isEvNewlyAdded,
  cardHasRfid,
} from 'features/cards/helpers/add-edit-card-helper';
import Env from 'Env';

import Label from 'constants/labels';

import { editCardDetails } from 'actions/cardActions';
import {
  useDispatchCardFormValues,
  useDispatchCardModalMeta,
  useDispatchGetPlasticType,
} from 'dispatchers/dispatchers';
import { useMemo, useState, useEffect } from 'react';
import {
  useSelectAccountDefaults,
  useSelectAssignedGrandfatherClientNumber,
  useSelectCardDetails,
} from 'selectors/selectors';
import { CardModalFormNames, CardModalFormValues } from '../types/CardModalFormNames';
import { useDispatch, useSelector } from 'react-redux';
import useConfirmExitModal from './useConfirmExitModal';
import { UserState } from 'types/UserState';

export default function useEditCardFormController(
  onClose: () => void,
  status?: string,
  modalType?: string,
  onEvReissueComplete?: () => void
) {
  const [valuesToConfirm, setValuesToConfirm] = useState({});
  const [isEvReissue, setIsEvReissue] = useState(false);
  // Set to true when the user confirms an EV reissue; useEffect defers the
  // onEvReissueComplete signal to after the confirm modal has fully closed.
  const [evReissueConfirmed, setEvReissueConfirmed] = useState(false);

  const dispatch = useDispatch();
  const dispatchCardFormValues = useDispatchCardFormValues();
  const dispatchPlasticType = useDispatchGetPlasticType();
  const dispatchCardModalMeta = useDispatchCardModalMeta();
  const userStore: UserState = useSelector((state: any) => state.userStore);

  const loggedInUser = userStore.loggedInUser;
  const assignedAccountNumber = userStore.assignedAccountNumber;
  const assignedGrandfatherClientNumber = useSelectAssignedGrandfatherClientNumber();
  const cardDetails = useSelectCardDetails();
  const accountDefaults = useSelectAccountDefaults();

  function submitNewCardDetails(values: any, skipClose = false) {
    const request = getAddEditCardRequest(values, cardDetails?.fleetLimiters, assignedGrandfatherClientNumber);

    // add card status if its present
    dispatch(
      editCardDetails(
        {
          ...request,
          setCardStatus: status,
        },
        assignedGrandfatherClientNumber
      )
    );
    if (modalType && modalType === 'account') {
      window.dataLayer.push({
        event: 'Card_Portal_Accounts_Card_Detail_Edit_Submit',
        button_click: true,
      });
    } else {
      window.dataLayer.push({
        event: 'Card_Portal_Individual_Card_Detail_Edit_Submit',
        button_click: true,
      });
    }
    trackCustomEvent('Submit Edit Card', loggedInUser, assignedAccountNumber, 'Clicked Submit to submit Edit Card.', {
      selectedAccount: request?.contractNumber,
      selectedCard: request?.contractIdentifier,
    });
    if (!skipClose) onClose();
  }

  // Opened directly via useModal so it works even when EditCardModal is rendered inside
  // another modal's content area. The useEffect-based AcknowledgeModal component does
  // not work when nested inside a design-system useModal content area.
  // When onEvReissueComplete is provided, signal the parent (cards-list.tsx) after
  // the confirm modal closes so it can render AcknowledgeModal at the top level.
  useEffect(() => {
    if (evReissueConfirmed) {
      setEvReissueConfirmed(false);
      onEvReissueComplete?.();
    }
  }, [evReissueConfirmed]);

  const reissueConfirmModal = useConfirmExitModal({
    title: Label.EDIT_CARD,
    content: 'You have made changes that will cause a reissue. Are you sure you want to continue?',
    confirmLabel: 'Confirm',
    onConfirm: () => {
      submitNewCardDetails(valuesToConfirm, isEvReissue);
      if (isEvReissue) {
        setEvReissueConfirmed(true);
      }
    },
  });

  useMemo(() => {
    const cd = cardDetails as any; // Record<string,unknown> from Redux — cast for typed dispatch
    if (!cd || !cd.cardNumber) return;
    dispatchCardModalMeta({
      dailyDollarLimits: cd?.dailyDollarLimits,
      monthlyDollarLimits: cd?.monthlyDollarLimits,
      fleetLimiters: cd?.fleetLimiters,
      vasRsaAccountCode: cd?.vasRsaAccountCode,
      vasSnrAccountCode: cd?.vasSnrAccountCode,
      disableAccountField: true,
      accountFlatList: true,
      disableEmbossingName: true,
      verifyPosPrompt:
        accountDefaults?.verifyPosPrompt !== 'Mandatory PIN'
          ? cd?.verifyPosPrompt
          : cd?.verifyPosPrompt === 'Mandatory PIN'
            ? accountDefaults?.verifyPosPrompt
            : cd?.verifyPosPrompt,
    });

    dispatchCardFormValues({
      ...getEditCardDetails(cd),
      ...getFleetLimiterCodesObj(cd?.fleetLimiters),
      [CardModalFormNames.AllProducts]: cd?.fleetLimiters?.every(
        (item) => item.available === false || item.selected === true
      ),
    });

    if (cd?.plasticTypeCode) {
      dispatchPlasticType(cd?.plasticTypeCode);
    }
  }, [cardDetails]);

  function onFormSubmit(values: CardModalFormValues) {
    if (Env.getIsEvWay4Enabled() && isEvNewlyAdded(values, cardDetails) && !cardHasRfid(cardDetails)) {
      setIsEvReissue(true);
      setValuesToConfirm(values);
      reissueConfirmModal.open();
    } else if (isReissueForm(cardDetails, values) && !status) {
      setIsEvReissue(false);
      // Pop-up a confirm modal if there are values changed that will cause a reissue
      setValuesToConfirm(values);
      reissueConfirmModal.open();
    } else {
      submitNewCardDetails(values);
    }
  }

  return {
    onFormSubmit,
    isLoading: false,
  };
}
